import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export function Reveal({ children, className }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={fadeUp}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function H2({ children }) {
  return (
    <Reveal className="mt-14 mb-4">
      <h2 className="font-headline-section text-[28px] md:text-headline-section text-on-surface dark:text-surface-bright">
        {children}
      </h2>
    </Reveal>
  );
}

export function H3({ children }) {
  return (
    <Reveal className="mt-8 mb-3">
      <h3 className="font-headline-card text-headline-card text-primary">{children}</h3>
    </Reveal>
  );
}

export function P({ children }) {
  return (
    <p className="font-body-md text-body-md text-on-surface-variant dark:text-surface-variant leading-relaxed mb-4">
      {children}
    </p>
  );
}

export function UL({ children }) {
  return <ul className="space-y-2 mb-4 list-none">{children}</ul>;
}

export function LI({ children }) {
  return (
    <li className="flex gap-3 font-body-md text-body-md text-on-surface-variant dark:text-surface-variant leading-relaxed">
      <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
      <span>{children}</span>
    </li>
  );
}

export function Code({ children }) {
  return (
    <pre className="mb-4 overflow-x-auto rounded-DEFAULT border border-glass bg-surface-dark px-4 py-3 font-data-mono text-[13px] text-emerald-300">
      <code>{children}</code>
    </pre>
  );
}

export function Callout({ children }) {
  return (
    <div className="mb-6 rounded-DEFAULT border border-primary/30 bg-primary/5 px-5 py-4 font-body-md text-sm text-on-surface dark:text-surface-bright leading-relaxed">
      {children}
    </div>
  );
}

export function Figure({ src, alt, caption }) {
  return (
    <Reveal className="my-8">
      <figure className="glass-card rounded-xl p-3 overflow-hidden">
        <img src={src} alt={alt} className="w-full h-auto rounded-DEFAULT" loading="lazy" />
        {caption && (
          <figcaption className="font-body-md text-xs text-on-surface-variant dark:text-surface-variant mt-3 px-2 pb-1">
            {caption}
          </figcaption>
        )}
      </figure>
    </Reveal>
  );
}

export function InfoTable({ rows }) {
  return (
    <Reveal className="mb-6">
      <div className="overflow-hidden rounded-DEFAULT border border-glass">
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([field, value], i) => (
              <tr key={field} className={i % 2 === 0 ? "bg-surface-container-low dark:bg-white/5" : "bg-transparent"}>
                <th className="text-left align-top px-4 py-3 font-headline-card text-sm text-on-surface dark:text-surface-bright w-1/3 border-b border-glass">
                  {field}
                </th>
                <td className="px-4 py-3 font-body-md text-on-surface-variant dark:text-surface-variant border-b border-glass">
                  {value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
  );
}

export function Table({ headers, rows }) {
  return (
    <Reveal className="mb-6">
      <div className="overflow-x-auto rounded-DEFAULT border border-glass">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-container dark:bg-white/10">
              {headers.map((header) => (
                <th
                  key={header}
                  className="text-left align-top px-4 py-3 font-label-sm text-label-sm uppercase tracking-wider text-on-surface dark:text-surface-bright border-b border-glass whitespace-nowrap"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-surface-container-low dark:bg-white/5" : "bg-transparent"}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className="px-4 py-3 font-body-md text-on-surface-variant dark:text-surface-variant border-b border-glass align-top min-w-[180px]"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
  );
}
